import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText, Plus, Search, RefreshCw, Eye, Euro,
  Calendar, Filter, ChevronLeft, ChevronRight, CheckCircle2, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatAxiosError = (e, fallback = 'Erreur') => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join('; ');
  }
  return fallback;
};

const CreditNotes = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const [creditNotes, setCreditNotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [consumptionFilter, setConsumptionFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createMode, setCreateMode] = useState('manual');
  const [currentPage, setCurrentPage] = useState(1);
  const [busyId, setBusyId] = useState(null);
  const itemsPerPage = 20;

  const [form, setForm] = useState({
    supplier_id: '',
    date_facture: new Date().toISOString().split('T')[0],
    montant_ttc: '',
    invoice_number: '',
    avoir_reference_invoice_id: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [notesRes, suppliersRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/supplier-invoices/?type_document=avoir&limit=500`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/suppliers/?limit=500`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/supplier-invoices/?type_document=facture&limit=500`).catch(() => ({ data: [] })),
      ]);
      setCreditNotes(notesRes.data || []);
      setSuppliers(suppliersRes.data || []);
      setInvoices(invoicesRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des avoirs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({
      supplier_id: '',
      date_facture: new Date().toISOString().split('T')[0],
      montant_ttc: '',
      invoice_number: '',
      avoir_reference_invoice_id: '',
      notes: '',
    });
    setCreateMode('manual');
  };

  const handleSelectInvoice = (invoiceId) => {
    const invoice = invoices.find(i => i.id === invoiceId);
    if (invoice) {
      setForm(prev => ({
        ...prev,
        avoir_reference_invoice_id: invoiceId,
        supplier_id: invoice.supplier_id || prev.supplier_id,
        montant_ttc: Math.abs(invoice.montant_ttc || 0).toString(),
      }));
    }
  };

  const handleCreate = async () => {
    if (!form.supplier_id || !form.montant_ttc) {
      toast.error('Veuillez remplir le fournisseur et le montant'); return;
    }
    try {
      const today = new Date().toISOString().slice(0, 10);
      await axios.post(`${API_URL}/supplier-invoices/`, {
        supplier_id: form.supplier_id,
        invoice_number: form.invoice_number || `AVO-${Date.now()}`,
        date_facture: form.date_facture,
        date_echeance: form.date_facture,
        date_reception: today,
        type_document: 'avoir',
        avoir_reference_invoice_id: form.avoir_reference_invoice_id || null,
        montant_ht: Math.abs(parseFloat(form.montant_ttc)) || 0,
        montant_tva: 0,
        montant_ttc: Math.abs(parseFloat(form.montant_ttc)) || 0,
        tva_breakdown: [],
        lignes: [],
        notes: form.notes || null,
      });
      toast.success('Avoir créé avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatAxiosError(error, "Erreur lors de la création de l'avoir"));
    }
  };

  const handleMarkUsed = async (avoirId) => {
    setBusyId(avoirId);
    try {
      await axios.put(`${API_URL}/supplier-invoices/${avoirId}/mark-used`);
      toast.success('Avoir marqué comme utilisé');
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur')); }
    finally { setBusyId(null); }
  };

  const handleUnmarkUsed = async (avoirId) => {
    if (!window.confirm("Réinitialiser l'utilisation de cet avoir ? Les liaisons aux factures seront retirées.")) return;
    setBusyId(avoirId);
    try {
      await axios.put(`${API_URL}/supplier-invoices/${avoirId}/unmark-used`);
      toast.success('Avoir réinitialisé');
      fetchData();
    } catch (e) { toast.error(formatAxiosError(e, 'Erreur')); }
    finally { setBusyId(null); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const formatCurrency = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const supplierName = (id) => suppliers.find(s => s.id === id)?.nom || '-';

  // Compute consumption status client-side (also returned by backend, but
  // re-derive in case the data was created before the feature shipped).
  const enrichNote = (n) => {
    const total = Math.abs(n.montant_ttc || 0);
    const used = n.avoir_used_amount || 0;
    let status = 'unused';
    if (total > 0 && used >= total - 0.01) status = 'used';
    else if (used > 0.001) status = 'partial';
    return { ...n, _total: total, _used: used, _remaining: Math.max(0, total - used), _consumption: status };
  };

  const enriched = useMemo(() => creditNotes.map(enrichNote), [creditNotes]);

  const filteredNotes = enriched.filter(n => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q
      || (n.internal_ref || '').toLowerCase().includes(q)
      || (n.invoice_number || '').toLowerCase().includes(q)
      || supplierName(n.supplier_id).toLowerCase().includes(q);
    const matchesConsumption = consumptionFilter === 'all' || n._consumption === consumptionFilter;
    return matchesSearch && matchesConsumption;
  });

  const totalPages = Math.ceil(filteredNotes.length / itemsPerPage);
  const paginatedNotes = filteredNotes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const totalUnused = enriched.filter(n => n._consumption !== 'used').reduce((s, n) => s + n._remaining, 0);
  const unusedCount = enriched.filter(n => n._consumption !== 'used').length;

  const consumptionBadge = (n) => {
    if (n._consumption === 'used') {
      return <Badge className="bg-slate-200 text-slate-700">Utilisé</Badge>;
    }
    if (n._consumption === 'partial') {
      return <Badge className="bg-amber-100 text-amber-800">Partiel — reste {formatCurrency(n._remaining)}</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-800">Non utilisé</Badge>;
  };

  const workflowBadge = (status) => {
    const map = {
      brouillon: { label: 'Brouillon', cls: 'bg-slate-100 text-slate-800' },
      en_validation: { label: 'En validation', cls: 'bg-blue-100 text-blue-800' },
      validee: { label: 'Validé', cls: 'bg-green-100 text-green-800' },
      rejetee: { label: 'Rejeté', cls: 'bg-red-100 text-red-800' },
      modification_demandee: { label: 'Modif. demandée', cls: 'bg-orange-100 text-orange-800' },
    };
    const c = map[status] || { label: status || '-', cls: 'bg-slate-100 text-slate-800' };
    return <Badge className={c.cls}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement des avoirs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Avoirs fournisseurs</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Crédits restants à imputer sur les factures fournisseurs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Actualiser</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { resetForm(); setShowCreateDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" />Nouvel avoir
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><FileText className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-sm text-slate-500">Total avoirs</p><p className="text-2xl font-bold">{enriched.length}</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg"><Euro className="w-5 h-5 text-emerald-700" /></div>
            <div>
              <p className="text-sm text-slate-500">Crédit disponible</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalUnused)}</p>
              <p className="text-xs text-slate-500">{unusedCount} avoir(s) non/partiellement utilisé(s)</p>
            </div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg"><Calendar className="w-5 h-5 text-slate-600" /></div>
            <div>
              <p className="text-sm text-slate-500">Ce mois</p>
              <p className="text-2xl font-bold">
                {enriched.filter(n => {
                  const d = new Date(n.date_facture);
                  const now = new Date();
                  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
          </div>
        </CardContent></Card>
      </div>

      <Card><CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Rechercher par référence ou fournisseur..." value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="pl-10" />
          </div>
          <Select value={consumptionFilter} onValueChange={(v) => { setConsumptionFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-48"><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="Consommation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes consommations</SelectItem>
              <SelectItem value="unused">Non utilisé</SelectItem>
              <SelectItem value="partial">Partiellement utilisé</SelectItem>
              <SelectItem value="used">Utilisé</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {filteredNotes.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg font-medium">Aucun avoir trouvé</p>
            <p className="text-slate-400 text-sm mt-1">Créez un nouvel avoir pour commencer</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Montant TTC</TableHead>
                  <TableHead>Workflow</TableHead>
                  <TableHead>Consommation</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedNotes.map(n => (
                  <TableRow key={n.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium font-mono cursor-pointer" onClick={() => navigate(`/invoices/${n.id}`)}>
                      {n.internal_ref || n.invoice_number || '-'}
                    </TableCell>
                    <TableCell>{n.supplier_name || supplierName(n.supplier_id)}</TableCell>
                    <TableCell>{formatDate(n.date_facture)}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(n._total)}</TableCell>
                    <TableCell>{workflowBadge(n.workflow_status)}</TableCell>
                    <TableCell>{consumptionBadge(n)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/invoices/${n.id}`)} title="Détails">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {hasPermission('invoices', 'pay') && n.workflow_status === 'validee' && n._consumption !== 'used' && (
                          <Button variant="ghost" size="sm" disabled={busyId === n.id}
                            onClick={() => handleMarkUsed(n.id)} title="Marquer comme utilisé">
                            <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                          </Button>
                        )}
                        {hasPermission('invoices', 'pay') && n._consumption !== 'unused' && (
                          <Button variant="ghost" size="sm" disabled={busyId === n.id}
                            onClick={() => handleUnmarkUsed(n.id)} title="Annuler utilisation">
                            <RotateCcw className="w-4 h-4 text-slate-700" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-slate-500">{filteredNotes.length} avoir(s) - Page {currentPage} / {totalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent></Card>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nouvel avoir fournisseur</DialogTitle></DialogHeader>

          <Tabs value={createMode} onValueChange={setCreateMode} className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="manual">Saisie manuelle</TabsTrigger>
              <TabsTrigger value="from_invoice">Depuis une facture</TabsTrigger>
            </TabsList>
            <TabsContent value="from_invoice" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Facture d'origine</Label>
                <Select value={form.avoir_reference_invoice_id} onValueChange={handleSelectInvoice}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner une facture..." /></SelectTrigger>
                  <SelectContent>
                    {invoices.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.internal_ref || inv.invoice_number} - {supplierName(inv.supplier_id)} - {formatCurrency(inv.montant_ttc)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>
            <TabsContent value="manual" className="mt-4"><div className="h-0" /></TabsContent>
          </Tabs>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fournisseur *</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Sélectionner un fournisseur..." /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.date_facture} onChange={(e) => setForm({ ...form, date_facture: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>N° de l'avoir</Label>
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="AV-2026-001" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Montant TTC *</Label>
              <Input type="number" step="0.01" value={form.montant_ttc}
                onChange={(e) => setForm({ ...form, montant_ttc: e.target.value })} placeholder="0.00" />
              <p className="text-xs text-slate-500">Saisissez le montant en positif. L'avoir représentera un crédit à imputer sur les futures factures.</p>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Motif de l'avoir..." />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate}>Créer l'avoir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditNotes;
